import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { type QuizDetail } from "~/api/types";
import { updateQuiz } from "~/api/v2/requests";
import { QuizReason } from "~/molecules/QuizReason";
import { Button } from "~/atoms/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Item, ItemActions, ItemContent } from "~/atoms/item";
import { Input } from "~/atoms/input";
import { Label } from "~/atoms/label";
import { RadioGroup, RadioGroupItem } from "~/atoms/radio-group";
import { Textarea } from "~/atoms/textarea";

interface EditableOption {
  text: string;
  isCorrect: boolean;
}

interface EditableQuestion {
  question: string;
  options: EditableOption[];
  /** Stored reason HTML. Editable only by removing it. */
  reason: string | null;
  /** Plain text for adding a new reason when there is none yet. */
  reasonDraft: string;
}

/** Escape a plain-text reason into the same restricted HTML the taker renders. */
const plainTextToHtml = (text: string): string | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const escape = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escape(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

interface Props {
  quiz: QuizDetail;
  onSaved: () => void;
}

const emptyQuestion = (): EditableQuestion => ({
  question: "",
  options: [
    { text: "", isCorrect: true },
    { text: "", isCorrect: false },
  ],
  reason: null,
  reasonDraft: "",
});

const isValid = (questions: EditableQuestion[]): boolean =>
  questions.length > 0 &&
  questions.every(
    (question) =>
      question.question.trim().length > 0 &&
      question.options.length >= 2 &&
      question.options.every((option) => option.text.trim().length > 0) &&
      question.options.filter((option) => option.isCorrect).length === 1,
  );

const QuizEditor = ({ quiz, onSaved }: Props) => {
  const { t } = useTranslation();
  const editorId = useId();
  const [questions, setQuestions] = useState<EditableQuestion[]>(() =>
    quiz.questions.map((question) => ({
      question: question.question,
      options: question.options.map((option) => ({ ...option })),
      reason: question.reason,
      reasonDraft: "",
    })),
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateQuestion = (
    index: number,
    update: (question: EditableQuestion) => EditableQuestion,
  ) =>
    setQuestions((previousQuestions) =>
      previousQuestions.map((question, questionIndex) =>
        questionIndex === index ? update(question) : question,
      ),
    );

  const setQuestionText = (index: number, text: string) =>
    updateQuestion(index, (question) => ({ ...question, question: text }));

  const setReasonDraft = (index: number, reasonDraft: string) =>
    updateQuestion(index, (question) => ({ ...question, reasonDraft }));

  const removeReason = (index: number) =>
    updateQuestion(index, (question) => ({
      ...question,
      reason: null,
      reasonDraft: "",
    }));

  const setOptionText = (
    questionIndex: number,
    optionIndex: number,
    text: string,
  ) =>
    updateQuestion(questionIndex, (question) => ({
      ...question,
      options: question.options.map((option, index) =>
        index === optionIndex ? { ...option, text } : option,
      ),
    }));

  const setCorrect = (questionIndex: number, optionIndex: number) =>
    updateQuestion(questionIndex, (question) => ({
      ...question,
      options: question.options.map((option, index) => ({
        ...option,
        isCorrect: index === optionIndex,
      })),
    }));

  const addOption = (questionIndex: number) =>
    updateQuestion(questionIndex, (question) => ({
      ...question,
      options: [...question.options, { text: "", isCorrect: false }],
    }));

  const removeOption = (questionIndex: number, optionIndex: number) =>
    updateQuestion(questionIndex, (question) => {
      const removedWasCorrect = question.options.some(
        (option, index) => index === optionIndex && option.isCorrect,
      );
      return {
        ...question,
        options: question.options
          .filter((option, index) => index !== optionIndex)
          // Keep exactly one correct option if the correct one was removed.
          .map((option, index) => ({
            ...option,
            isCorrect: removedWasCorrect ? index === 0 : option.isCorrect,
          })),
      };
    });

  const addQuestion = () =>
    setQuestions((previousQuestions) => [
      ...previousQuestions,
      emptyQuestion(),
    ]);

  const removeQuestion = (index: number) =>
    setQuestions((previousQuestions) =>
      previousQuestions.filter(
        (question, questionIndex) => questionIndex !== index,
      ),
    );

  const save = async () => {
    setIsSaving(true);
    try {
      await updateQuiz(
        quiz.id,
        questions.map((question) => ({
          question: question.question.trim(),
          options: question.options.map((option) => ({
            text: option.text.trim(),
            isCorrect: option.isCorrect,
          })),
          reason: question.reason ?? plainTextToHtml(question.reasonDraft),
        })),
      );
      onSaved();
      toast.success(t(($) => $.anchoring.quiz.edit.saved));
    } catch {
      toast.error(t(($) => $.anchoring.quiz.edit.error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {questions.map((question, questionIndex) => (
        <Card key={questionIndex}>
          <CardHeader>
            <CardTitle>
              <Label htmlFor={`${editorId}-question-${questionIndex}`}>
                {t(($) => $.anchoring.quiz.edit.questionLabel, {
                  number: questionIndex + 1,
                })}
              </Label>
            </CardTitle>
            <CardAction>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t(($) => $.anchoring.quiz.edit.removeQuestion)}
                disabled={questions.length <= 1}
                onClick={() => removeQuestion(questionIndex)}
              >
                <Trash2 />
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Textarea
              id={`${editorId}-question-${questionIndex}`}
              value={question.question}
              onChange={(event) =>
                setQuestionText(questionIndex, event.target.value)
              }
              placeholder={t(($) => $.anchoring.quiz.edit.questionPlaceholder)}
            />

            <div className="flex flex-col gap-2">
              <Label id={`${editorId}-options-${questionIndex}`}>
                {t(($) => $.anchoring.quiz.edit.optionsLabel)}
              </Label>
              <RadioGroup
                aria-labelledby={`${editorId}-options-${questionIndex}`}
                value={String(
                  question.options.findIndex((option) => option.isCorrect),
                )}
                onValueChange={(value) =>
                  setCorrect(questionIndex, Number(value))
                }
              >
                {question.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={String(optionIndex)}
                      aria-label={t(
                        ($) => $.anchoring.quiz.edit.correctOption,
                        { number: optionIndex + 1 },
                      )}
                    />
                    <Input
                      value={option.text}
                      onChange={(event) =>
                        setOptionText(
                          questionIndex,
                          optionIndex,
                          event.target.value,
                        )
                      }
                      placeholder={t(
                        ($) => $.anchoring.quiz.edit.optionPlaceholder,
                      )}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t(($) => $.anchoring.quiz.edit.removeOption)}
                      disabled={question.options.length <= 2}
                      onClick={() => removeOption(questionIndex, optionIndex)}
                    >
                      <X />
                    </Button>
                  </div>
                ))}
              </RadioGroup>
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                onClick={() => addOption(questionIndex)}
              >
                <Plus data-icon="inline-start" />
                {t(($) => $.anchoring.quiz.edit.addOption)}
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={`${editorId}-reason-${questionIndex}`}>
                {t(($) => $.anchoring.quiz.edit.reasonLabel)}
              </Label>
              {question.reason ? (
                <Item variant="muted" className="items-start">
                  <ItemContent>
                    <QuizReason html={question.reason} />
                  </ItemContent>
                  <ItemActions>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t(($) => $.anchoring.quiz.edit.removeReason)}
                      onClick={() => removeReason(questionIndex)}
                    >
                      <Trash2 />
                    </Button>
                  </ItemActions>
                </Item>
              ) : (
                <Textarea
                  id={`${editorId}-reason-${questionIndex}`}
                  value={question.reasonDraft}
                  onChange={(event) =>
                    setReasonDraft(questionIndex, event.target.value)
                  }
                  placeholder={t(
                    ($) => $.anchoring.quiz.edit.reasonPlaceholder,
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={addQuestion}>
          <Plus data-icon="inline-start" />
          {t(($) => $.anchoring.quiz.edit.addQuestion)}
        </Button>
        <Button
          onClick={() => void save()}
          disabled={!isValid(questions) || isSaving}
        >
          {isSaving && (
            <Loader2 data-icon="inline-start" className="animate-spin" />
          )}
          {t(($) => $.anchoring.quiz.edit.save)}
        </Button>
      </div>
    </div>
  );
};

export default QuizEditor;
